import {
    DependenciesPayload,
    IWebSDKLocale,
} from '@sxm-de/dev-kits/web-sdk/types';
import { GetCampaignContentFnResponse } from '../types/campaign';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';

export async function GetCampaignContent(
    { contentfulSdk }: DependenciesPayload,
    { campaignId, locale }: { campaignId: string; locale: IWebSDKLocale },
): Promise<GetCampaignContentFnResponse> {
    try {
        const campaignsByLocale: GetCampaignContentFnResponse = {};
        for (const locale1 of locale.supported) {
            const data = await contentfulSdk?.getCampaign({
                name: campaignId,
                locale: locale1,
                include: '10',
                select: 'fields.name,fields.hero,fields.confirmation_hero,fields.confirmation_hero_theme,fields.post_purchase_primary_cta_component,sys.id',
            });
            const campaign = mapCampaignData(data, 'https://www.siriusxm.com');
            campaignsByLocale[locale1] = {
                campaignId,
                ...campaign,
            };
        }
        return campaignsByLocale;
    } catch (error: any) {
        /**
         * TODO:
         * Log error to error logger (not analytics).
         */
        throw new Error('SYSTEM');
    }
}

function mapCampaignData(data: any, baseUrlCDN: string) {
    const item = data?.items?.[0];
    const heroId = item?.fields?.hero?.sys?.id;
    const hero = data?.includes?.Entry?.find((i: any) => i?.sys?.id === heroId);
    const foregroundImageUrl = hero?.fields?.foreground_image?.url;
    const backgroundImageUrl = hero?.fields?.background_image?.url;
    const imageAltText = hero?.fields?.foreground_image?.altText;

    const confirmationHero = getEntry(
        data,
        item?.fields?.confirmation_hero?.sys?.id,
    );
    const confirmation = confirmationHero
        ? {
              ...getImageData(confirmationHero, baseUrlCDN),
              headlineHtml: documentToHtmlString(
                  confirmationHero?.fields?.headline,
                  {
                      preserveWhitespace: true,
                  },
              ),
              theme:
                  confirmationHero?.fields?.confirmation_hero_theme || 'Theme1',
          }
        : undefined;

    const postPurchasePrimaryCtaComponent = getEntry(
        data,
        item?.fields?.post_purchase_primary_cta_component?.sys?.id,
    );
    const cta = getEntry(
        data,
        postPurchasePrimaryCtaComponent?.fields?.cta?.sys?.id,
    );
    const mobileCta = getEntry(
        data,
        postPurchasePrimaryCtaComponent?.fields?.mobile_cta?.sys?.id,
    );
    const streamingPostPurchase =
        postPurchasePrimaryCtaComponent && cta && mobileCta
            ? {
                  headline: postPurchasePrimaryCtaComponent?.fields?.headline,
                  headlineTheme:
                      postPurchasePrimaryCtaComponent?.fields?.headline_theme ||
                      'Theme1',
                  descriptionHtml: documentToHtmlString(
                      postPurchasePrimaryCtaComponent?.fields?.description,
                      {
                          preserveWhitespace: true,
                      },
                  ),
                  image: postPurchasePrimaryCtaComponent?.fields?.image,
                  imageUrl:
                      postPurchasePrimaryCtaComponent?.fields?.[
                          'image_override'
                      ]?.url,
                  cta: getLink(cta),
                  ctaMobile: getLink(mobileCta),
              }
            : undefined;

    return {
        foregroundImageUrl: foregroundImageUrl
            ? `${baseUrlCDN}${foregroundImageUrl}`
            : '',
        backgroundImageUrl: backgroundImageUrl
            ? `${baseUrlCDN}${backgroundImageUrl}`
            : '',
        imageAltText,
        confirmation,
        streamingPostPurchase,
    };
}

function getEntry(data: any, id: any) {
    return data?.includes?.Entry?.find((i: any) => i.sys?.id === id);
}

function getImageData(item: any, baseUrlCDN: string) {
    const foregroundImageUrl = item?.fields?.foreground_image?.url;
    const backgroundImageUrl = item?.fields?.background_image?.url;
    return {
        foregroundImageUrl: foregroundImageUrl
            ? `${baseUrlCDN}${foregroundImageUrl}`
            : '',
        backgroundImageUrl: backgroundImageUrl
            ? `${baseUrlCDN}${backgroundImageUrl}`
            : '',
        imageAltText: item?.fields?.foreground_image?.altText || '',
    };
}

function getLink(item: any) {
    return {
        href: item?.fields?.href,
        linkText: item?.fields?.link_text,
        ariaLabel: item?.fields?.aria_label,
    };
}
